import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HelmetProvider } from 'react-helmet-async';
import axios from 'axios';
import './i18n';
import '@/App.css';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import SEO, { SEO_CONFIG } from '@/components/SEO';
import { 
  Heart, 
  Sparkles, 
  Play, 
  Users, 
  Clock, 
  Star, 
  TrendingUp,
  Dumbbell,
  Wind,
  Brain,
  Activity,
  User,
  LogOut,
  Calendar,
  BookOpen,
  Globe,
  Shield,
  Crown,
  Apple,
  Sun,
  Flame,
  Moon,
  Footprints,
  MessageCircle
} from 'lucide-react';
import { HowItWorks } from '@/components/HowItWorks';
import { FAQ } from '@/components/FAQ';
import { SecurePayment } from '@/components/SecurePayment';
import { Footer } from '@/components/Footer';
import { PWAInstallPrompt } from '@/components/PWAInstallPrompt';
import { BrandVoice } from '@/components/BrandVoice';
import { SimpleModal } from '@/components/SimpleModal';
import { InstructorsPage } from '@/pages/InstructorsPage';
import { PremiumPage } from '@/pages/PremiumPage';
import { LessonDetail } from '@/pages/LessonDetail';
import AdminPanel from '@/pages/AdminPanel';
import InstructorDashboard from '@/pages/InstructorDashboard';
import InstructorProfile from '@/pages/InstructorProfile';
import CourseBuilder from '@/pages/CourseBuilder';
import TransactionHistory from '@/pages/TransactionHistory';
import BecomeInstructor from '@/pages/BecomeInstructor';
import ApplicationStatus from '@/pages/ApplicationStatus';
import MyCourses from '@/pages/MyCourses';
import WatchCourse from '@/pages/WatchCourse';
import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import PaymentSuccess from '@/pages/PaymentSuccess';
// Instructor Panel Pages
import StudentManagement from '@/pages/StudentManagement';
import CalendarManagement from '@/pages/CalendarManagement';
import EarningsAnalytics from '@/pages/EarningsAnalytics';
import ReferralDashboard from '@/pages/ReferralDashboard';
import InstructorLanding from '@/pages/InstructorLanding';
// AI Dietitian Pages
import AIDietitian from '@/pages/AIDietitian';
import CalorieCheck from '@/pages/CalorieCheck';
import WeeklyPlan from '@/pages/WeeklyPlan';
import AIDietitianChat from '@/pages/AIDietitianChat';
import AIGoals from '@/pages/AIGoals';
import FoodAnalyzer from '@/pages/FoodAnalyzer';
// Legal & Contact Pages
import ContactPage from '@/pages/ContactPage';
import KVKKPage from '@/pages/KVKKPage';
import TermsPage from '@/pages/TermsPage';
import PrivacyPage from '@/pages/PrivacyPage';
import AboutPage from '@/pages/AboutPage';
import RefundPage from '@/pages/RefundPage';
// P0: New Pages
import DailyCheckin from '@/pages/DailyCheckin';
import PricingPage from '@/pages/PricingPage';
// P0: AI Concierge Widget
import AIConcierge from '@/components/AIConcierge';
// P1: New Pages
import ActivityTracker from '@/pages/ActivityTracker';
import SleepTracker from '@/pages/SleepTracker';
import StreaksPage from '@/pages/StreaksPage';
// P0-P1: Community & Engagement Features
import CommunityPage from '@/pages/CommunityPage';
import MessagesPage from '@/pages/MessagesPage';
import AICoachPage from '@/pages/AICoachPage';
import LeaderboardPage from '@/pages/LeaderboardPage';
import MissionsPage from '@/pages/MissionsPage';

// Use relative path for API calls to avoid CORS issues on production domain
const API = '/api';

// Auth Context
const AuthContext = React.createContext(null);

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [isInitialized, setIsInitialized] = useState(false);

  const fetchUser = React.useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      // Don't logout automatically to prevent infinite loops
      if (token) {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
        delete axios.defaults.headers.common['Authorization'];
      }
    } finally {
      setIsInitialized(true);
    }
  }, [token]);

  useEffect(() => {
    if (token && !isInitialized) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else if (!token) {
      setIsInitialized(true);
    }
  }, [token, isInitialized, fetchUser]);

  const login = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Navigation Component
const Navigation = () => {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [authMode, setAuthMode] = useState('login');

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  // Smart navigation for "Become Instructor" button
  const handleBecomeInstructor = () => {
    if (!user) {
      // Not logged in -> go to signup with instructor pre-selected
      navigate('/register?role=instructor');
    } else if (user.role === 'instructor') {
      // Already instructor -> go to dashboard
      navigate('/instructor/dashboard');
    } else {
      // Logged in but not instructor -> go to application
      navigate('/become-instructor');
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass-effect" data-testid="main-navigation">
      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-6">
        <div className="flex justify-between items-center h-16">
          {/* Logo - Far left */}
          <Link to="/" className="flex items-center space-x-1.5 shrink-0 mr-4" data-testid="logo-link">
            <img 
              src="/icons/pilatika-logo-static.svg" 
              alt="Pilatika Logo" 
              className="h-8 w-8"
            />
            <span className="text-xl font-bold text-gradient tracking-tight">Pilatika</span>
          </Link>

          {/* Desktop Navigation - Compact layout */}
          <div className="hidden lg:flex items-center space-x-4 flex-nowrap flex-1 justify-center">
            <Link to="/" className="text-gray-700 hover:text-purple-600 transition-colors text-sm whitespace-nowrap" data-testid="nav-home">{t('home')}</Link>
            
            {/* Dersler - Çok Yakında */}
            <Link to="/lessons" className="text-gray-400 hover:text-purple-600 transition-colors text-xs whitespace-nowrap flex items-center gap-1" data-testid="nav-lessons">
              Dersler
              <span className="text-[10px] px-1 py-0.5 rounded bg-orange-100 text-orange-600">Yakında</span>
            </Link>
            
            {/* Eğitmenler - Çok Yakında */}
            <Link to="/instructors" className="text-gray-400 hover:text-purple-600 transition-colors text-xs whitespace-nowrap flex items-center gap-1" data-testid="nav-instructors">
              Eğitmenler
              <span className="text-[10px] px-1 py-0.5 rounded bg-orange-100 text-orange-600">Yakında</span>
            </Link>
            
            {/* Daily Check-in Link */}
            {user && (
              <Link 
                to="/daily-checkin" 
                className="text-orange-600 hover:text-orange-800 font-medium flex items-center transition-colors text-sm whitespace-nowrap"
                data-testid="nav-checkin"
              >
                <Sun className="h-3 w-3 mr-1" />
                Check-in
              </Link>
            )}
            
            {/* Community Link */}
            <Link 
              to="/community" 
              className="text-purple-600 hover:text-purple-800 font-medium flex items-center transition-colors text-sm whitespace-nowrap"
              data-testid="nav-community"
            >
              <Users className="h-3 w-3 mr-1" />
              Topluluk
            </Link>
            
            {/* AI Coach Link */}
            {user && (
              <Link 
                to="/ai-coach" 
                className="text-indigo-600 hover:text-indigo-800 font-medium flex items-center transition-colors text-sm whitespace-nowrap"
                data-testid="nav-ai-coach"
              >
                <Sparkles className="h-3 w-3 mr-1" />
                AI Koç
              </Link>
            )}
            
            {/* AI Dietitian Link */}
            <Link 
              to="/ai-dietitian" 
              className="text-green-600 hover:text-green-800 font-medium flex items-center transition-colors text-sm whitespace-nowrap"
              data-testid="nav-ai-dietitian"
            >
              <Apple className="h-3 w-3 mr-1" />
              Diyetisyen
            </Link>
            
            <Link to="/pricing" className="text-purple-600 hover:text-purple-800 font-medium flex items-center transition-colors text-sm whitespace-nowrap" data-testid="nav-pricing">
              <Crown className="h-3 w-3 mr-1" />
              Planlar
            </Link>
            {user?.role === 'admin' && (
              <Link to="/admin" className="text-red-600 hover:text-red-800 font-medium transition-colors text-sm whitespace-nowrap" data-testid="nav-admin">
                Admin
              </Link>
            )}
          </div>

          {/* Right side - Auth buttons and Language selector (moved to end) */}
          <div className="flex items-center space-x-2 shrink-0">
            <Select value={i18n.language} onValueChange={changeLanguage}>
              <SelectTrigger className="w-[90px] sm:w-[100px] h-9" data-testid="language-selector">
                <Globe className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tr">Türkçe</SelectItem>
                <SelectItem value="en">English</SelectItem>
              </SelectContent>
            </Select>

            {user ? (
              <div className="flex items-center space-x-1 sm:space-x-3">
                {/* My Courses Link */}
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => navigate('/my-courses')} 
                  className="hidden sm:flex text-purple-600 hover:text-purple-800"
                >
                  <BookOpen className="h-4 w-4 mr-1" />
                  <span className="hidden lg:inline">Kurslarım</span>
                </Button>
                <Button variant="ghost" size="sm" onClick={() => navigate('/profile')} data-testid="profile-button" className="hidden sm:flex">
                  <User className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                  <span className="hidden md:inline">{user.full_name}</span>
                </Button>
                <Button variant="ghost" size="sm" onClick={() => navigate('/profile')} data-testid="profile-button-mobile" className="sm:hidden">
                  <User className="h-5 w-5" />
                </Button>
                <Button variant="ghost" size="sm" onClick={logout} data-testid="logout-button">
                  <LogOut className="h-4 w-4 sm:h-5 sm:w-5" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-1 sm:gap-2">
                <Link to="/login">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="font-semibold text-xs sm:text-sm px-2 sm:px-4"
                    data-testid="login-button"
                  >
                    Giriş
                  </Button>
                </Link>
                
                <Link to="/register">
                  <Button 
                    className="btn-primary font-semibold text-xs sm:text-sm px-2 sm:px-4 h-8 sm:h-9"
                    data-testid="register-button"
                  >
                    Kayıt Ol
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

// Auth Dialog Component
const AuthDialog = ({ onSuccess, initialMode = 'login' }) => {
  const { t } = useTranslation();
  const { login } = useAuth();
  const [isLogin, setIsLogin] = useState(initialMode === 'login');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const response = await axios.post(`${API}${endpoint}`, formData);
      
      login(response.data.token, response.data.user);
      toast.success(isLogin ? t('loginSuccess') : t('registerSuccess'));
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'An error occurred');
    }
  };

  return (
    <div data-testid="auth-form">
      <div className="space-y-3 mb-6">
        <h2 className="text-2xl font-bold text-center">
          {isLogin ? t('loginTitle') : t('registerTitle')}
        </h2>
        <p className="text-center text-base text-gray-600">
          {isLogin ? t('loginDescription') : t('registerDescription')}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5 mt-6">
        {!isLogin && (
          <div className="space-y-2">
            <Label htmlFor="full_name" className="text-sm font-semibold">
              {t('fullName')} <span className="text-red-500">*</span>
            </Label>
            <Input
              id="full_name"
              placeholder="Örn: Ahmet Yılmaz"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              required={!isLogin}
              className="h-11"
              data-testid="input-fullname"
            />
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="email" className="text-sm font-semibold">
            {t('email')} <span className="text-red-500">*</span>
          </Label>
          <Input
            id="email"
            type="email"
            placeholder="ornek@email.com"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
            className="h-11"
            data-testid="input-email"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password" className="text-sm font-semibold">
            {t('password')} <span className="text-red-500">*</span>
          </Label>
          <Input
            id="password"
            type="password"
            placeholder="En az 6 karakter"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            required
            minLength={6}
            className="h-11"
            data-testid="input-password"
          />
        </div>

        <Button type="submit" className="w-full btn-primary h-12 text-base font-semibold" data-testid="auth-submit-button">
          {isLogin ? '🔐 ' + t('login') : '✨ ' + t('register')}
        </Button>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="bg-white px-4 text-gray-500">{t('or')}</span>
          </div>
        </div>

        <div className="text-center space-y-3">
          <p className="text-sm text-gray-600">
            {isLogin ? t('noAccount') : t('alreadyHaveAccount')}
          </p>
          <Button
            type="button"
            variant="outline"
            onClick={() => setIsLogin(!isLogin)}
            className="w-full h-11 font-semibold"
            data-testid="toggle-auth-mode"
          >
            {isLogin ? '📝 ' + t('register') : '🔑 ' + t('login')}
          </Button>
        </div>
      </form>
    </div>
  );
};

// Home Page
const HomePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [featuredLessons, setFeaturedLessons] = useState([]);
  const [featuredInstructors, setFeaturedInstructors] = useState([]);

  // Smart navigation for "Become Instructor" button
  const handleBecomeInstructor = () => {
    if (!user) {
      navigate('/register?role=instructor');
    } else if (user.role === 'instructor') {
      navigate('/instructor/dashboard');
    } else {
      navigate('/become-instructor');
    }
  };

  const fetchFeaturedLessons = React.useCallback(async () => {
    try {
      const response = await axios.get(`${API}/lessons?featured=true`);
      setFeaturedLessons(response.data.slice(0, 6));
    } catch (error) {
      console.error('Failed to fetch lessons:', error);
    }
  }, []);

  const fetchFeaturedInstructors = React.useCallback(async () => {
    try {
      const response = await axios.get(`${API}/instructor/featured`);
      setFeaturedInstructors(response.data);
    } catch (error) {
      console.error('Failed to fetch instructors:', error);
    }
  }, []);

  useEffect(() => {
    fetchFeaturedLessons();
    fetchFeaturedInstructors();
  }, [fetchFeaturedLessons, fetchFeaturedInstructors]);

  const categories = [
    { name: 'pilates', icon: Activity, color: 'bg-purple-100 text-purple-600' },
    { name: 'yoga', icon: Users, color: 'bg-pink-100 text-pink-600' },
    { name: 'meditation', icon: Brain, color: 'bg-blue-100 text-blue-600' },
    { name: 'fitness', icon: Dumbbell, color: 'bg-orange-100 text-orange-600' },
    { name: 'breathing', icon: Wind, color: 'bg-teal-100 text-teal-600' },
    { name: 'posture', icon: TrendingUp, color: 'bg-green-100 text-green-600' }
  ];

  return (
    <div className="wellness-gradient" data-testid="home-page">
      <SEO {...SEO_CONFIG.home} />
      {/* Hero Section */}
      <section className="hero-section flex items-center relative pt-24" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full relative z-10">
          <div className="text-center">
            <div className="inline-block mb-4 mt-4">
              <img 
                src="/icons/pilatika-logo.svg" 
                alt="Pilatika" 
                className="h-20 w-20 mx-auto"
              />
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              {t('heroTitle')}
            </h1>
            <p className="text-base sm:text-lg text-white/90 mb-4 max-w-3xl mx-auto">
              Move. Flow. Evolve.
            </p>
            <p className="text-base sm:text-lg text-white/80 mb-8 max-w-3xl mx-auto">
              {t('heroSubtitle')}
            </p>
            <Button 
              size="lg" 
              className="btn-primary text-lg px-8 py-6 mb-6"
              onClick={() => navigate('/ai-coach')}
              data-testid="get-started-button"
            >
              <Sparkles className="mr-2 h-5 w-5" />
              AI Koç ile Başla
            </Button>
            
            {/* AI Features Quick Links */}
            <div className="flex flex-wrap justify-center gap-3 mb-6">
              <Button 
                size="sm" 
                variant="outline"
                className="bg-white/90 hover:bg-white text-green-700 border-white/50"
                onClick={() => navigate('/ai-dietitian')}
              >
                <Apple className="mr-1 h-4 w-4" />
                AI Diyetisyen
              </Button>
              <Button 
                size="sm" 
                variant="outline"
                className="bg-white/90 hover:bg-white text-orange-700 border-white/50"
                onClick={() => navigate('/daily-checkin')}
              >
                <Sun className="mr-1 h-4 w-4" />
                Günlük Check-in
              </Button>
            </div>
            
            {/* Become Instructor Button */}
            <div className="mb-6">
              <Button 
                size="lg" 
                variant="outline"
                className="bg-white/90 hover:bg-white text-purple-700 border-2 border-white/50 text-base px-6 py-5"
                onClick={handleBecomeInstructor}
                data-testid="become-instructor-hero-button"
              >
                <Users className="mr-2 h-5 w-5" />
                {user?.role === 'instructor' ? t('instructorDashboard') : t('becomeInstructor')}
              </Button>
            </div>
            
            <div className="flex items-center justify-center text-sm text-white/80 mt-4">
              <Shield className="h-4 w-4 mr-2 text-green-300" />
              <span>{t('trustBadge')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Coming Soon Banner - Büyük İçerikler Yolda */}
      <section className="py-6 bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-center md:text-left">
            <div className="flex items-center gap-3">
              <span className="text-4xl">🚀</span>
              <div>
                <h3 className="text-xl md:text-2xl font-bold text-white">Büyük içerikler yolda!</h3>
                <p className="text-white/90 text-sm md:text-base">
                  Pilatika eğitmenleri yeni derslerini çekiyor.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <p className="text-white/90 text-sm md:text-base">
                Şimdiden <span className="font-semibold text-white">AI destekli koçluk, diyetisyen ve motivasyon</span> sistemimizi keşfedin.
              </p>
              <Button 
                variant="secondary" 
                className="bg-white text-purple-700 hover:bg-white/90 font-semibold"
                onClick={() => navigate('/ai-dietitian')}
              >
                Keşfet
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Wellness Lifestyle Gallery */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Sağlıklı Yaşam Topluluğumuz</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Her gün binlerce kişi Pilatika ile sağlıklı bir yaşam için adım atıyor</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="relative group overflow-hidden rounded-2xl aspect-square">
              <img 
                src="https://customer-assets.emergentagent.com/job_b1ad3fc8-8921-44aa-909c-3f315950b679/artifacts/g6alz9l4_image.png"
                alt="Pilates sınıfı"
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </div>
            <div className="relative group overflow-hidden rounded-2xl aspect-square">
              <img 
                src="https://customer-assets.emergentagent.com/job_b1ad3fc8-8921-44aa-909c-3f315950b679/artifacts/duh7oscd_image.png"
                alt="Yoga seansı"
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </div>
            <div className="relative group overflow-hidden rounded-2xl aspect-square">
              <img 
                src="https://customer-assets.emergentagent.com/job_b1ad3fc8-8921-44aa-909c-3f315950b679/artifacts/eml7qurm_image.png"
                alt="Esneme egzersizi"
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </div>
            <div className="relative group overflow-hidden rounded-2xl aspect-square">
              <img 
                src="https://customer-assets.emergentagent.com/job_b1ad3fc8-8921-44aa-909c-3f315950b679/artifacts/qf0ahjkl_image.png"
                alt="Wellness aktivite"
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </div>
          </div>
        </div>
      </section>

      {/* Brand Voice */}
      <BrandVoice />

      {/* How It Works */}
      <HowItWorks />

      {/* Categories */}
      <section className="py-20 bg-white" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4">{t('allCategories')}</h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">{t('categoriesSubtitle')}</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {categories.map((cat) => {
              const Icon = cat.icon;
              return (
                <div
                  key={cat.name}
                  onClick={() => navigate(`/lessons?category=${cat.name}`)}
                  className="category-card cursor-pointer"
                  data-testid={`category-${cat.name}`}
                >
                  <Card className="text-center hover:shadow-xl">
                    <CardContent className="pt-6">
                      <div className={`${cat.color} w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4`}>
                        <Icon className="h-8 w-8" />
                      </div>
                      <p className="font-semibold">{t(cat.name)}</p>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Lessons */}
      <section className="py-20 wellness-gradient" data-testid="featured-lessons-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">{t('featuredLessons')}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredLessons.map((lesson) => (
              <Card key={lesson.id} className="lesson-card" data-testid={`lesson-card-${lesson.id}`}>
                <CardHeader>
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex gap-2">
                      <Badge variant="secondary" data-testid={`lesson-type-${lesson.id}`}>{t(lesson.type)}</Badge>
                    </div>
                    <Badge variant="outline" data-testid={`lesson-level-${lesson.id}`}>{t(lesson.level)}</Badge>
                  </div>
                  <CardTitle className="text-xl" data-testid={`lesson-title-${lesson.id}`}>{lesson.title}</CardTitle>
                  <CardDescription data-testid={`lesson-description-${lesson.id}`}>{lesson.description.substring(0, 100)}...</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <Clock className="h-4 w-4 mr-1" />
                      <span data-testid={`lesson-duration-${lesson.id}`}>{lesson.duration} {t('minutes')}</span>
                    </div>
                    <div className="text-lg font-bold text-purple-600" data-testid={`lesson-price-${lesson.id}`}>
                      ₺{lesson.price}
                    </div>
                  </div>
                  {lesson.instructor && (
                    <div className="flex items-center mb-4">
                      <Avatar className="h-8 w-8 mr-2">
                        <AvatarFallback>{lesson.instructor.full_name[0]}</AvatarFallback>
                      </Avatar>
                      <span className="text-sm" data-testid={`lesson-instructor-${lesson.id}`}>{lesson.instructor.full_name}</span>
                    </div>
                  )}
                  <Button 
                    className="w-full btn-primary"
                    onClick={() => navigate(`/lessons/${lesson.id}`)}
                    data-testid={`lesson-view-button-${lesson.id}`}
                  >
                    {t('viewDetails')}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Instructors */}
      <section className="py-20 bg-white" data-testid="featured-instructors-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4">{t('featuredInstructors')}</h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">{t('featuredInstructorsSubtitle')}</p>
          <div className="grid md:grid-cols-3 lg:grid-cols-6 gap-8">
            {featuredInstructors.map((instructor) => (
              <div 
                key={instructor.user_id} 
                className="instructor-card cursor-pointer text-center"
                onClick={() => navigate(`/instructors/${instructor.user_id}`)}
                data-testid={`instructor-card-${instructor.user_id}`}
              >
                <Avatar className="h-24 w-24 mx-auto mb-4 instructor-avatar">
                  {instructor.avatar_url ? (
                    <AvatarImage src={instructor.avatar_url} alt={instructor.user?.full_name} />
                  ) : (
                    <AvatarFallback className="text-2xl">{instructor.user?.full_name[0]}</AvatarFallback>
                  )}
                </Avatar>
                <h3 className="font-semibold mb-1" data-testid={`instructor-name-${instructor.user_id}`}>
                  {instructor.user?.full_name}
                </h3>
                <p className="text-sm text-gray-600" data-testid={`instructor-specialty-${instructor.user_id}`}>
                  {instructor.specialties?.[0]}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Secure Payment */}
      <SecurePayment />

      {/* SEO Content Section - 700+ kelime özgün içerik */}
      <section className="py-16 bg-gradient-to-b from-white to-purple-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Pilatika Nedir? */}
          <article className="mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
              Pilatika Nedir? - Türkiye'nin AI Destekli Wellness Platformu
            </h2>
            <div className="prose prose-lg text-gray-700 space-y-4">
              <p>
                <strong>Pilatika</strong>, Türkiye'nin ilk ve tek yapay zeka destekli online wellness platformudur. 
                Pilates, yoga, fitness ve meditasyon alanlarında uzman eğitmenlerle dijital dersleri bir araya getirirken, 
                AI teknolojisini kullanarak kullanıcılarına kişiselleştirilmiş sağlıklı yaşam deneyimi sunar. 
                Evden çıkmadan, istediğiniz saatte, kendi hızınızda wellness yolculuğunuza başlayabilirsiniz.
              </p>
              <p>
                Platformumuz, geleneksel fitness uygulamalarından farklı olarak <strong>7/24 erişilebilir AI Koç</strong> ve 
                <strong> AI Diyetisyen</strong> özellikleriyle donatılmıştır. Bu yapay zeka asistanları, sorularınızı yanıtlar, 
                motivasyonunuzu artırır ve beslenme önerileri sunar. Türk mutfağına uygun, sağlıklı ve lezzetli tariflerle 
                diyet programınızı destekler.
              </p>
            </div>
          </article>

          {/* Eğitmenler İçin */}
          <article className="mb-12 bg-white rounded-2xl p-8 shadow-sm border border-purple-100">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
              Eğitmenler İçin Pilatika - Online Ders Vererek Gelir Kazan
            </h2>
            <div className="prose prose-lg text-gray-700 space-y-4">
              <p>
                Pilates, yoga veya fitness eğitmeni misiniz? <strong>Pilatika</strong> ile uzmanlığınızı gelire dönüştürün! 
                Platformumuz, sertifikalı eğitmenlere binlerce öğrenciye ulaşma imkanı sunar. 
                Kendi programınızı oluşturun, canlı veya kayıtlı dersler verin ve esnek çalışma saatleriyle 
                tutkununuzu kariyer haline getirin.
              </p>
              <p>
                Eğitmenlerimiz, satışlarından <strong>%80 gelir payı</strong> alır. iyzico altyapısıyla güvenli ödeme sistemi, 
                otomatik faturalama ve şeffaf kazanç takibi sunuyoruz. Eğitmen olmak için 
                <Link to="/become-instructor" className="text-purple-600 hover:text-purple-800 font-semibold"> buradan başvurabilirsiniz</Link>.
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Kendi fiyatınızı belirleyin</li>
                <li>İstediğiniz zaman ders ekleyin</li>
                <li>Canlı veya kayıtlı içerik oluşturun</li>
                <li>Öğrenci yorumları ve puanlamalarla itibar kazanın</li>
                <li>Detaylı analitik paneli ile performansınızı takip edin</li>
              </ul>
            </div>
          </article>

          {/* Öğrenciler İçin */}
          <article className="mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
              Öğrenciler İçin Pilatika - Sağlıklı Yaşamın Kapıları Açık
            </h2>
            <div className="prose prose-lg text-gray-700 space-y-4">
              <p>
                Sağlıklı bir yaşam tarzına adım atmak istiyorsanız, <strong>Pilatika</strong> tam size göre! 
                Ücretsiz üyelik ile platformumuza katılabilir, AI Koç ve günlük check-in özelliklerini 
                hemen kullanmaya başlayabilirsiniz. Premium üyelik ile tüm eğitmen derslerine, 
                AI Diyetisyen'e ve özel içeriklere sınırsız erişim kazanırsınız.
              </p>
              <p>
                <Link to="/register" className="text-purple-600 hover:text-purple-800 font-semibold">Ücretsiz üye olun</Link> ve 
                wellness yolculuğunuza bugün başlayın. İlk adımınız için AI Koçumuz sizi bekliyor!
              </p>
            </div>
          </article>

          {/* Nasıl Çalışır? */}
          <article className="mb-12 bg-gradient-to-r from-purple-600 to-purple-800 rounded-2xl p-8 text-white">
            <h2 className="text-2xl sm:text-3xl font-bold mb-6">
              Pilatika Nasıl Çalışır?
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="bg-white/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">1</span>
                </div>
                <h3 className="font-semibold text-lg mb-2">Üye Ol</h3>
                <p className="text-white/90 text-sm">
                  E-posta adresinizle ücretsiz hesap oluşturun. Kredi kartı gerekmez.
                </p>
              </div>
              <div className="text-center">
                <div className="bg-white/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">2</span>
                </div>
                <h3 className="font-semibold text-lg mb-2">Keşfet</h3>
                <p className="text-white/90 text-sm">
                  AI Koç ile konuşun, günlük check-in yapın, eğitmen derslerini inceleyin.
                </p>
              </div>
              <div className="text-center">
                <div className="bg-white/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">3</span>
                </div>
                <h3 className="font-semibold text-lg mb-2">Dönüşün</h3>
                <p className="text-white/90 text-sm">
                  Düzenli pratik ile sağlıklı alışkanlıklar edinin, hedeflerinize ulaşın.
                </p>
              </div>
            </div>
          </article>

          {/* Neden Pilatika? */}
          <article className="mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
              Neden Pilatika? - Diğer Platformlardan Farkımız
            </h2>
            <div className="prose prose-lg text-gray-700 space-y-4">
              <p>
                Piyasada birçok fitness uygulaması var, ancak <strong>Pilatika</strong>'yı benzersiz kılan özellikler:
              </p>
              <div className="grid md:grid-cols-2 gap-4 not-prose">
                <div className="bg-purple-50 p-4 rounded-xl">
                  <h3 className="font-semibold text-purple-900 mb-2">🤖 AI Destekli Koçluk</h3>
                  <p className="text-sm text-gray-700">
                    7/24 erişilebilir yapay zeka koçunuz sorularınızı yanıtlar, motivasyonunuzu artırır.
                  </p>
                </div>
                <div className="bg-green-50 p-4 rounded-xl">
                  <h3 className="font-semibold text-green-900 mb-2">🥗 Türk Mutfağı Odaklı Diyetisyen</h3>
                  <p className="text-sm text-gray-700">
                    Yabancı tarifler yerine, Türk damak tadına uygun sağlıklı beslenme önerileri.
                  </p>
                </div>
                <div className="bg-blue-50 p-4 rounded-xl">
                  <h3 className="font-semibold text-blue-900 mb-2">👨‍🏫 Sertifikalı Uzman Eğitmenler</h3>
                  <p className="text-sm text-gray-700">
                    Tüm eğitmenlerimiz alanında deneyimli ve sertifika sahibi profesyonellerdir.
                  </p>
                </div>
                <div className="bg-orange-50 p-4 rounded-xl">
                  <h3 className="font-semibold text-orange-900 mb-2">🔒 Güvenli Ödeme</h3>
                  <p className="text-sm text-gray-700">
                    iyzico altyapısıyla banka seviyesinde güvenlik. Kredi kartı bilgileriniz bizde saklanmaz.
                  </p>
                </div>
              </div>
            </div>
          </article>

          {/* Internal Links */}
          <article className="bg-gray-50 rounded-2xl p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              Pilatika'yı Keşfedin
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Link to="/ai-coach" className="flex items-center gap-2 text-purple-600 hover:text-purple-800 font-medium">
                <Sparkles className="h-4 w-4" /> AI Koç
              </Link>
              <Link to="/ai-dietitian" className="flex items-center gap-2 text-green-600 hover:text-green-800 font-medium">
                <Apple className="h-4 w-4" /> AI Diyetisyen
              </Link>
              <Link to="/pricing" className="flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium">
                <Crown className="h-4 w-4" /> Fiyatlar
              </Link>
              <Link to="/become-instructor" className="flex items-center gap-2 text-orange-600 hover:text-orange-800 font-medium">
                <Users className="h-4 w-4" /> Eğitmen Ol
              </Link>
              <Link to="/about" className="flex items-center gap-2 text-gray-600 hover:text-gray-800 font-medium">
                <Heart className="h-4 w-4" /> Hakkımızda
              </Link>
              <Link to="/contact" className="flex items-center gap-2 text-gray-600 hover:text-gray-800 font-medium">
                <MessageCircle className="h-4 w-4" /> İletişim
              </Link>
              <Link to="/lessons" className="flex items-center gap-2 text-gray-600 hover:text-gray-800 font-medium">
                <Play className="h-4 w-4" /> Dersler
              </Link>
              <Link to="/instructors" className="flex items-center gap-2 text-gray-600 hover:text-gray-800 font-medium">
                <Users className="h-4 w-4" /> Eğitmenler
              </Link>
            </div>
          </article>

        </div>
      </section>

      {/* FAQ */}
      <FAQ />

      {/* Footer */}
      <Footer />
    </div>
  );
};

// Lessons Page - Marketplace
const LessonsPage = () => {
  const navigate = useNavigate();
  
  // Coming Soon Page - Eğitmen dersleri henüz hazır değil
  return (
    <div className="min-h-screen pt-20 bg-gradient-to-b from-purple-50 via-white to-purple-50" data-testid="lessons-page">
      <SEO {...SEO_CONFIG.lessons} />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Coming Soon Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full mb-6">
            <span className="text-5xl">🎓</span>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Eğitmen Dersleri Çok Yakında
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-2">
            Uzman eğitmenlerimiz yeni derslerini çekiyor.
          </p>
          <p className="text-gray-500 max-w-2xl mx-auto">
            Bu süreçte AI Koç, AI Diyetisyen, günlük check-in ve motivasyon özellikleri aktif olarak kullanılabilir.
          </p>
        </div>

        {/* Active Features Grid */}
        <div className="mb-12">
          <h2 className="text-xl font-semibold text-center text-gray-800 mb-6">
            ✨ Şimdi Aktif Özellikler
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {/* AI Koç */}
            <Card 
              className="cursor-pointer hover:shadow-lg transition-all hover:-translate-y-1 border-indigo-200 bg-gradient-to-br from-indigo-50 to-purple-50"
              onClick={() => navigate('/ai-coach')}
            >
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-indigo-100 rounded-xl">
                    <Sparkles className="h-6 w-6 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-gray-900">AI Koç</h3>
                    <p className="text-gray-600 text-sm">Kişisel motivasyon ve hedef takibi</p>
                    <Badge className="mt-2 bg-green-100 text-green-700">Aktif ✓</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* AI Diyetisyen */}
            <Card 
              className="cursor-pointer hover:shadow-lg transition-all hover:-translate-y-1 border-green-200 bg-gradient-to-br from-green-50 to-emerald-50"
              onClick={() => navigate('/ai-dietitian')}
            >
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-green-100 rounded-xl">
                    <Apple className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-gray-900">AI Diyetisyen</h3>
                    <p className="text-gray-600 text-sm">Kişiselleştirilmiş beslenme önerileri</p>
                    <Badge className="mt-2 bg-green-100 text-green-700">Aktif ✓</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Günlük Check-in */}
            <Card 
              className="cursor-pointer hover:shadow-lg transition-all hover:-translate-y-1 border-orange-200 bg-gradient-to-br from-orange-50 to-yellow-50"
              onClick={() => navigate('/daily-checkin')}
            >
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-orange-100 rounded-xl">
                    <Sun className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-gray-900">Günlük Check-in</h3>
                    <p className="text-gray-600 text-sm">Ruh halinizi takip edin, öneriler alın</p>
                    <Badge className="mt-2 bg-green-100 text-green-700">Aktif ✓</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Aktivite Takibi */}
            <Card 
              className="cursor-pointer hover:shadow-lg transition-all hover:-translate-y-1 border-blue-200 bg-gradient-to-br from-blue-50 to-cyan-50"
              onClick={() => navigate('/activity')}
            >
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-blue-100 rounded-xl">
                    <Activity className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-gray-900">Aktivite Takibi</h3>
                    <p className="text-gray-600 text-sm">Adım, yürüyüş ve egzersiz kayıtları</p>
                    <Badge className="mt-2 bg-green-100 text-green-700">Aktif ✓</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Notify Me Section */}
        <div className="text-center bg-white rounded-2xl p-8 shadow-sm border">
          <h3 className="font-semibold text-lg text-gray-900 mb-2">
            🔔 Dersler yayına girdiğinde haberdar ol
          </h3>
          <p className="text-gray-600 mb-4 text-sm">
            Üye olarak bekleyin, yeni dersler eklendiğinde bildirim alacaksınız.
          </p>
          <Button 
            className="btn-primary"
            onClick={() => navigate('/register')}
          >
            Üye Ol
          </Button>
        </div>
      </div>
    </div>
  );
};

// Wrapper components for Login/Register to access AuthContext
const LoginPageWrapper = () => {
  const { login } = useAuth();
  return <LoginPage onLogin={login} />;
};

const RegisterPageWrapper = () => {
  const { login } = useAuth();
  return <RegisterPage onLogin={login} />;
};

// Profile Page
const ProfilePage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);

  const fetchBookings = React.useCallback(async () => {
    try {
      const response = await axios.get(`${API}/bookings/my-bookings`);
      setBookings(response.data);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    }
  }, []);

  useEffect(() => {
    if (user?.id) {
      fetchBookings();
    }
  }, [user?.id, fetchBookings]);

  if (!user) return <Navigate to="/" />;

  return (
    <div className="min-h-screen pt-20 wellness-gradient" data-testid="profile-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">{user.full_name}</h1>
          <p className="text-gray-600">{user.email}</p>
        </div>

        <Tabs defaultValue="bookings" data-testid="profile-tabs">
          <TabsList>
            <TabsTrigger value="bookings" data-testid="tab-bookings">{t('myBookings')}</TabsTrigger>
            <TabsTrigger value="programs" data-testid="tab-programs">{t('myPrograms')}</TabsTrigger>
          </TabsList>

          <TabsContent value="bookings" className="mt-6" data-testid="bookings-content">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {bookings.map((booking) => (
                <Card key={booking.id} data-testid={`booking-card-${booking.id}`}>
                  <CardHeader>
                    <CardTitle>{booking.lesson?.title}</CardTitle>
                    <CardDescription>
                      <Badge variant={booking.payment_status === 'completed' ? 'success' : 'secondary'}>
                        {booking.payment_status}
                      </Badge>
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600">
                      Booked: {new Date(booking.created_at).toLocaleDateString()}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="programs" data-testid="programs-content">
            <p className="text-gray-600">Your AI programs will appear here</p>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// Main App Component
function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <div className="App">
            <Navigation />
            <Toaster position="top-right" />
            <PWAInstallPrompt />
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/lessons" element={<LessonsPage />} />
              <Route path="/lessons/:id" element={<LessonDetail />} />
              <Route path="/instructors" element={<InstructorsPage />} />
              <Route path="/instructors/:id" element={<InstructorProfile />} />
              <Route path="/premium" element={<PremiumPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/instructor/dashboard" element={<InstructorDashboard />} />
            <Route path="/instructor/profile" element={<InstructorProfile />} />
            <Route path="/instructor/courses/new" element={<CourseBuilder />} />
            <Route path="/instructor/courses/:id/edit" element={<CourseBuilder />} />
            <Route path="/instructor/transactions" element={<TransactionHistory />} />
            <Route path="/instructor/students" element={<StudentManagement />} />
            <Route path="/instructor/calendar" element={<CalendarManagement />} />
            <Route path="/instructor/analytics" element={<EarningsAnalytics />} />
            <Route path="/instructor/referral" element={<ReferralDashboard />} />
            <Route path="/egitmen/:username" element={<InstructorLanding />} />
            <Route path="/become-instructor" element={<BecomeInstructor />} />
            <Route path="/application-status" element={<ApplicationStatus />} />
            <Route path="/my-courses" element={<MyCourses />} />
            <Route path="/watch/:id" element={<WatchCourse />} />
            <Route path="/admin" element={<AdminPanel />} />
            <Route path="/login" element={<LoginPageWrapper />} />
            <Route path="/register" element={<RegisterPageWrapper />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            {/* P0: Daily Check-in */}
            <Route path="/daily-checkin" element={<DailyCheckin />} />
            <Route path="/checkin" element={<DailyCheckin />} />
            {/* P1: Activity & Sleep & Streaks */}
            <Route path="/activity" element={<ActivityTracker />} />
            <Route path="/sleep" element={<SleepTracker />} />
            <Route path="/streaks" element={<StreaksPage />} />
            {/* Community & Engagement Routes */}
            <Route path="/community" element={<CommunityPage />} />
            <Route path="/messages" element={<MessagesPage />} />
            <Route path="/ai-coach" element={<AICoachPage />} />
            <Route path="/leaderboard" element={<LeaderboardPage />} />
            <Route path="/missions" element={<MissionsPage />} />
            {/* AI Dietitian Routes */}
            <Route path="/ai-dietitian" element={<AIDietitian />} />
            <Route path="/ai-dietitian/calorie-check" element={<CalorieCheck />} />
            <Route path="/ai-dietitian/weekly-plan" element={<WeeklyPlan />} />
            <Route path="/ai-dietitian/chat" element={<AIDietitianChat />} />
            <Route path="/ai-dietitian/goals" element={<AIGoals />} />
            <Route path="/ai-dietitian/food-analyzer" element={<FoodAnalyzer />} />
            {/* Legal & Contact Pages */}
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/kvkk" element={<KVKKPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/refund" element={<RefundPage />} />
          </Routes>
          {/* P0: AI Concierge Widget - Global */}
          <AIConcierge />
        </div>
      </BrowserRouter>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;